import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  Shield, 
  User, 
  Database,
  Key,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface DiagnosticCheck {
  name: string;
  status: 'pass' | 'fail' | 'warning' | 'loading';
  message: string;
  hint?: string;
}

const AdminDiagnosticsPage = () => {
  const { user: authUser, session } = useAuth();
  const { user: adminUser } = useAdminAuth();
  const [checks, setChecks] = useState<DiagnosticCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [profileData, setProfileData] = useState<any>(null);

  const runDiagnostics = async () => {
    setLoading(true);
    const results: DiagnosticCheck[] = [];

    // 1. Session check
    if (session) {
      results.push({
        name: 'Active Session',
        status: 'pass',
        message: `Session active until ${new Date(session.expires_at! * 1000).toLocaleString()}`,
      });
    } else {
      results.push({
        name: 'Active Session',
        status: 'fail',
        message: 'No active session detected',
        hint: 'Try logging out and logging back in.',
      });
    }

    // 2. Auth user check
    if (authUser) {
      results.push({
        name: 'Auth User',
        status: 'pass',
        message: `Authenticated as ${authUser.email}`,
      });
    } else {
      results.push({
        name: 'Auth User',
        status: 'fail',
        message: 'No auth user found',
        hint: 'You may need to log in again.',
      });
    }

    // 3. Fetch roles from user_roles table (bypasses RLS issues)
    try {
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authUser?.id || '');

      if (rolesError) {
        results.push({
          name: 'User Roles (user_roles table)',
          status: 'fail',
          message: `Error: ${rolesError.message}`,
          hint: rolesError.message.includes('infinite recursion') 
            ? 'RLS policy on user_roles has circular reference. Contact admin.'
            : 'Check RLS policies on user_roles table.',
        });
      } else if (rolesData && rolesData.length > 0) {
        const roles = rolesData.map(r => r.role);
        setUserRoles(roles);
        results.push({
          name: 'User Roles (user_roles table)',
          status: 'pass',
          message: `Roles: ${roles.join(', ')}`,
        });
      } else {
        results.push({
          name: 'User Roles (user_roles table)',
          status: 'warning',
          message: 'No roles found in user_roles table',
          hint: 'A super_admin should assign you a role via User Roles page.',
        });
      }
    } catch (e: any) {
      results.push({
        name: 'User Roles (user_roles table)',
        status: 'fail',
        message: `Exception: ${e.message}`,
        hint: 'Unexpected error fetching roles.',
      });
    }

    // 4. Fetch profile data
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, role, agency_id, is_active')
        .eq('user_id', authUser?.id || '')
        .maybeSingle();

      if (profileError) {
        results.push({
          name: 'Profile Access',
          status: 'fail',
          message: `Error: ${profileError.message}`,
          hint: profileError.message.includes('infinite recursion')
            ? 'RLS policy on profiles has circular reference. The get_my_agency_id() helper may need repair.'
            : 'Check RLS policies on profiles table.',
        });
      } else if (profile) {
        setProfileData(profile);
        results.push({
          name: 'Profile Access',
          status: 'pass',
          message: `Profile found: ${profile.email} (role: ${profile.role}, active: ${profile.is_active})`,
        });
      } else {
        results.push({
          name: 'Profile Access',
          status: 'warning',
          message: 'No profile found for current user',
          hint: 'Your profile may not exist. A super_admin should create it.',
        });
      }
    } catch (e: any) {
      results.push({
        name: 'Profile Access',
        status: 'fail',
        message: `Exception: ${e.message}`,
        hint: 'Unexpected error fetching profile.',
      });
    }

    // 5. Test has_role function via a simple query
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: authUser?.id || '',
        _role: 'super_admin',
      });

      if (error) {
        results.push({
          name: 'has_role() Function',
          status: 'fail',
          message: `Error: ${error.message}`,
          hint: 'The has_role() SECURITY DEFINER function may be missing or broken.',
        });
      } else {
        results.push({
          name: 'has_role() Function',
          status: 'pass',
          message: `Function works. is_super_admin: ${data}`,
        });
      }
    } catch (e: any) {
      results.push({
        name: 'has_role() Function',
        status: 'fail',
        message: `Exception: ${e.message}`,
        hint: 'Check that public.has_role() exists and is SECURITY DEFINER.',
      });
    }

    // 6. Test get_my_agency_id function
    try {
      const { data, error } = await supabase.rpc('get_my_agency_id');

      if (error) {
        results.push({
          name: 'get_my_agency_id() Function',
          status: 'fail',
          message: `Error: ${error.message}`,
          hint: 'This helper prevents RLS recursion on profiles. It may need repair.',
        });
      } else {
        results.push({
          name: 'get_my_agency_id() Function',
          status: data ? 'pass' : 'warning',
          message: data ? `Your agency_id: ${data}` : 'No agency_id returned (you may not have an active profile with agency)',
          hint: data ? undefined : 'If you expect an agency, check your profile is_active and agency_id.',
        });
      }
    } catch (e: any) {
      results.push({
        name: 'get_my_agency_id() Function',
        status: 'fail',
        message: `Exception: ${e.message}`,
        hint: 'Check that public.get_my_agency_id() exists.',
      });
    }

    // 7. AdminAuth context check
    if (adminUser) {
      results.push({
        name: 'Admin Auth Context',
        status: 'pass',
        message: `Admin context loaded: ${adminUser.email} (role: ${adminUser.role})`,
      });
    } else {
      results.push({
        name: 'Admin Auth Context',
        status: 'warning',
        message: 'AdminAuth context has no user',
        hint: 'If you are an admin, try Force Logout and log in again.',
      });
    }

    setChecks(results);
    setLoading(false);
  };

  useEffect(() => {
    runDiagnostics();
  }, [authUser, session]);

  const getStatusIcon = (status: DiagnosticCheck['status']) => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'fail':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'loading':
        return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: DiagnosticCheck['status']) => {
    switch (status) {
      case 'pass':
        return <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20">Pass</Badge>;
      case 'fail':
        return <Badge variant="destructive">Fail</Badge>;
      case 'warning':
        return <Badge className="bg-amber-500/10 text-amber-600 hover:bg-amber-500/20">Warning</Badge>;
      case 'loading':
        return <Badge variant="secondary">Checking...</Badge>;
    }
  };

  const passCount = checks.filter(c => c.status === 'pass').length;
  const failCount = checks.filter(c => c.status === 'fail').length;
  const warnCount = checks.filter(c => c.status === 'warning').length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Auth & RLS Diagnostics
            </h1>
            <p className="text-muted-foreground mt-1">
              Read-only checks for authentication and Row-Level Security health
            </p>
          </div>
          <Button onClick={runDiagnostics} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Re-run Checks
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={cn("border-l-4", failCount > 0 ? "border-l-destructive" : "border-l-emerald-500")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overall Health</p>
                  <p className="text-2xl font-bold">
                    {failCount > 0 ? 'Issues Found' : warnCount > 0 ? 'Warnings' : 'Healthy'}
                  </p>
                </div>
                {failCount > 0 ? (
                  <XCircle className="h-10 w-10 text-destructive" />
                ) : warnCount > 0 ? (
                  <AlertTriangle className="h-10 w-10 text-amber-500" />
                ) : (
                  <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <User className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Current User</p>
                  <p className="font-medium truncate">{authUser?.email || 'Not logged in'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Key className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Detected Roles</p>
                  <div className="flex gap-1 flex-wrap">
                    {userRoles.length > 0 ? userRoles.map(role => (
                      <Badge key={role} variant="outline" className="capitalize">
                        {role.replace('_', ' ')}
                      </Badge>
                    )) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Check Results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Diagnostic Checks
            </CardTitle>
            <CardDescription>
              {passCount} passed, {failCount} failed, {warnCount} warnings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Running diagnostics...</span>
              </div>
            ) : (
              checks.map((check, idx) => (
                <div key={idx}>
                  <div className="flex items-start gap-3">
                    {getStatusIcon(check.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{check.name}</span>
                        {getStatusBadge(check.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{check.message}</p>
                      {check.hint && (
                        <Alert className="mt-2 py-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle className="text-sm">Hint</AlertTitle>
                          <AlertDescription className="text-sm">{check.hint}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                  {idx < checks.length - 1 && <Separator className="my-4" />}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Raw Data (for debugging) */}
        <Card>
          <CardHeader>
            <CardTitle>Raw Data (Debug)</CardTitle>
            <CardDescription>Session and profile data for troubleshooting</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Auth User ID</h4>
                <code className="text-xs bg-muted p-2 rounded block overflow-auto">
                  {authUser?.id || 'null'}
                </code>
              </div>
              <div>
                <h4 className="font-medium mb-2">Session Expires</h4>
                <code className="text-xs bg-muted p-2 rounded block overflow-auto">
                  {session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'null'}
                </code>
              </div>
              <div>
                <h4 className="font-medium mb-2">Profile Data</h4>
                <pre className="text-xs bg-muted p-2 rounded block overflow-auto max-h-32">
                  {JSON.stringify(profileData, null, 2) || 'null'}
                </pre>
              </div>
              <div>
                <h4 className="font-medium mb-2">AdminAuth User</h4>
                <pre className="text-xs bg-muted p-2 rounded block overflow-auto max-h-32">
                  {JSON.stringify(adminUser ? { email: adminUser.email, role: adminUser.role, agency_id: adminUser.agency_id } : null, null, 2)}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDiagnosticsPage;
